import { c as _c } from "react/compiler-runtime";
import { BaseErrorBoundary, type BaseErrorBoundaryProps } from "@repo/common/components";
import { ErrorBoundaryFallback } from "./error-boundary-fallback";
type ErrorBoundaryProps = Omit<BaseErrorBoundaryProps, "fallback"> & {
  /**
   * Whether to show the error message in the fallback UI.
   * @default false
   */
  showErrorMessage?: boolean;
};

/**
 * ErrorBoundary component with default styled UI fallback.
 *
 * This is a convenience wrapper around the BaseErrorBoundary from `@repo/common`
 * that provides a styled error UI using Alert, Button, and Text components.
 *
 * For custom fallback UI, use BaseErrorBoundary from `@repo/common/components` directly.
 *
 * @example
 * ```tsx
 * import { ErrorBoundary } from "@repo/ui/components/ui/error-boundary";
 *
 * <ErrorBoundary showErrorMessage>
 *   {children}
 * </ErrorBoundary>
 * ```
 */
export function ErrorBoundary(t0) {
  const $ = _c(10);
  let children;
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      children,
      showErrorMessage: t1,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = props;
    $[3] = t1;
  } else {
    children = $[1];
    props = $[2];
    t1 = $[3];
  }
  const showErrorMessage = t1 === undefined ? false : t1;
  let t2;
  if ($[4] !== showErrorMessage) {
    t2 = fallbackProps => <ErrorBoundaryFallback {...fallbackProps} showErrorMessage={showErrorMessage} />;
    $[4] = showErrorMessage;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== children || $[7] !== props || $[8] !== t2) {
    t3 = <BaseErrorBoundary {...props} fallback={t2}>{children}</BaseErrorBoundary>;
    $[6] = children;
    $[7] = props;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  return t3;
}

/**
 * Higher-order component that wraps a component with an ErrorBoundary
 */
export function withErrorBoundary<P extends object>(Component: React.ComponentType<P>, options?: {
  showErrorMessage?: boolean;
}): React.FC<P> {
  const displayName = Component.displayName || Component.name || "Component";
  const WrappedComponent: React.FC<P> = props => {
    const $ = _c(2);
    let t0;
    if ($[0] !== props) {
      t0 = <ErrorBoundary showErrorMessage={options?.showErrorMessage}><Component {...props} /></ErrorBoundary>;
      $[0] = props;
      $[1] = t0;
    } else {
      t0 = $[1];
    }
    return t0;
  };
  WrappedComponent.displayName = `withErrorBoundary(${displayName})`;
  return WrappedComponent;
}